import type { Group, MeResponse, SuggestItem } from '@stalvi/contracts';
import { isEan13 } from '@stalvi/contracts';
import { useMutation } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { GroupMembersGrid } from '../components/GroupMembers';
import { GroupsNav } from '../components/GroupsNav';
import { Header } from '../components/Header';
import { useAbortRef } from '../hooks/useAbortRef';
import { isAbortError } from '../lib/abort';
import { ApiError, api } from '../lib/api';

const DEBOUNCE_MS = 250;
const MIN_QUERY = 4;

interface Selection {
  ean13: string;
  name: string | null;
}

function SelectedList({ items, onRemove }: { items: Selection[]; onRemove: (ean13: string) => void }) {
  if (items.length === 0)
    return <p className="text-sm text-text-tertiary">Agrega al menos 2 GTINs para crear un grupo.</p>;
  return (
    <ul className="flex flex-col gap-2" data-testid="selection-list">
      {items.map((s) => (
        <li
          key={s.ean13}
          className="flex items-center justify-between gap-2 border border-border-subtle rounded-md px-3 py-2"
        >
          <span className="min-w-0">
            <span className="font-medium truncate block">{s.name ?? '—'}</span>
            <span className="mono text-xs text-text-tertiary">{s.ean13}</span>
          </span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-label={`Quitar ${s.ean13}`}
            onClick={() => onRemove(s.ean13)}
          >
            <X size={16} />
          </button>
        </li>
      ))}
    </ul>
  );
}

export function GroupsNew({ me, onSignOut }: { me: MeResponse; onSignOut: () => void }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SuggestItem[]>([]);
  const [selected, setSelected] = useState<Selection[]>([]);
  const [created, setCreated] = useState<Group | null>(null);
  const { next: nextAbort } = useAbortRef();

  const create = useMutation({
    mutationFn: (ean13s: string[]) => api.createGroup(ean13s),
    onSuccess: (res) => {
      setCreated(res.group);
      setSelected([]);
      setQ('');
      setResults([]);
    },
  });

  useEffect(() => {
    if (q.trim().length < MIN_QUERY) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => {
      const ac = nextAbort();
      api
        .suggest(q.trim(), ac.signal)
        .then((r) => setResults(r.items))
        .catch((e) => {
          if (!isAbortError(e)) setResults([]);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q, nextAbort]);

  function add(ean13: string, name: string | null) {
    setCreated(null);
    create.reset();
    setSelected((prev) => (prev.some((s) => s.ean13 === ean13) ? prev : [...prev, { ean13, name }]));
  }

  function remove(ean13: string) {
    setSelected((prev) => prev.filter((s) => s.ean13 !== ean13));
  }

  function addTyped() {
    const raw = q.trim();
    if (isEan13(raw)) {
      add(raw, null);
      setQ('');
    }
  }

  const error = create.error;
  const errorMessage = error instanceof ApiError ? error.apiMessage : error ? 'Hubo un problema' : null;

  return (
    <div className="stalvi-app flex flex-col">
      <Header me={me} onSignOut={onSignOut} />
      <GroupsNav />
      <main className="flex-1 px-6 py-6 overflow-auto">
        <div className="max-w-3xl mx-auto flex flex-col gap-6">
          <h1 className="text-xl font-semibold">Crear grupo manual</h1>

          <section className="card p-4">
            <label htmlFor="group-search" className="block mb-2 text-sm text-text-secondary">
              Busca productos o pega un GTIN de 13 dígitos
            </label>
            <div className="flex gap-2">
              <input
                id="group-search"
                className="input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Nombre, marca o EAN13"
                autoComplete="off"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTyped();
                  }
                }}
              />
              {isEan13(q.trim()) && (
                <button type="button" className="btn btn-secondary" onClick={addTyped}>
                  <Plus size={16} /> Agregar
                </button>
              )}
            </div>

            {results.length > 0 && (
              <ul className="mt-3 divide-y divide-border-subtle border border-border-subtle rounded-md">
                {results.map((it) => (
                  <li key={it.productId} className="flex items-center justify-between gap-3 px-3 py-2">
                    <span className="min-w-0">
                      <span className="font-medium truncate block">{it.name}</span>
                      <span className="text-sm text-text-secondary truncate">{it.brand ?? '—'}</span>
                    </span>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm shrink-0"
                      disabled={!it.ean13}
                      onClick={() => it.ean13 && add(it.ean13, it.name)}
                    >
                      <Plus size={16} /> {it.ean13 ?? 'sin GTIN'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="card p-4 flex flex-col gap-4">
            <h2 className="font-semibold">Selección ({selected.length})</h2>
            <SelectedList items={selected} onRemove={remove} />
            {errorMessage && (
              <p role="alert" className="text-danger text-sm">
                {errorMessage}
              </p>
            )}
            <button
              type="button"
              className="btn btn-primary self-start"
              disabled={selected.length < 2 || create.isPending}
              onClick={() => create.mutate(selected.map((s) => s.ean13))}
            >
              Crear grupo
            </button>
          </section>

          {created && (
            <section className="card p-4 flex flex-col gap-3" data-testid="created-group">
              <p className="text-success font-medium">Grupo {created.groupId} creado.</p>
              <GroupMembersGrid members={created.members} />
            </section>
          )}
        </div>
      </main>
    </div>
  );
}
